import { useState } from 'react';
import { Modal } from './Modal';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';

/**
 * App-styled replacements for `window.prompt` / `window.confirm`, driven from
 * the store so any code path can raise one. The native dialogs blocked the
 * whole tab, ignored the app's theme and language, and are suppressed outright
 * in some embedded/PWA contexts.
 */

/** Single-line text prompt. Resolves with the entered value, or `null` if cancelled. */
export function PromptDialog() {
  const t = useT();
  const req = useStore((s) => s.prompt);
  const setPrompt = useStore((s) => s.setPrompt);
  const [value, setValue] = useState('');
  const [seen, setSeen] = useState<symbol | null>(null);

  // Seed the field once per request rather than in an effect, so opening the
  // dialog never flashes the previous request's value.
  if (req && seen !== req.key) {
    setSeen(req.key);
    setValue(req.initial ?? '');
    return null;
  }
  if (!req) return null;

  const settle = (result: string | null) => {
    req.resolve(result);
    setPrompt(null);
  };

  return (
    <Modal onClose={() => settle(null)} title={req.title}>
      <form
        className="mb-field"
        onSubmit={(e) => {
          e.preventDefault();
          settle(value.trim() ? value.trim() : null);
        }}
      >
        <input
          className="mb-modal-url"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={req.title}
          autoFocus
        />
        <div className="mb-modal-actions">
          <button type="button" className="mb-btn" onClick={() => settle(null)}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="mb-btn mb-btn-primary" disabled={!value.trim()}>
            {req.confirmLabel ?? t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Yes/no confirmation. Resolves `true` only when the user confirms. */
export function ConfirmDialog() {
  const t = useT();
  const req = useStore((s) => s.confirm);
  const setConfirm = useStore((s) => s.setConfirm);
  if (!req) return null;

  const settle = (ok: boolean) => {
    req.resolve(ok);
    setConfirm(null);
  };

  return (
    <Modal onClose={() => settle(false)} title={req.title}>
      {req.body && <p className="mb-modal-note">{req.body}</p>}
      <div className="mb-modal-actions">
        {/* A notice has nothing to decline, so it gets no Cancel. */}
        {!req.acknowledge && (
          <button type="button" className="mb-btn" onClick={() => settle(false)}>
            {t('common.cancel')}
          </button>
        )}
        <button
          type="button"
          className={`mb-btn ${req.danger ? 'mb-btn-danger' : 'mb-btn-primary'}`}
          onClick={() => settle(true)}
        >
          {req.confirmLabel ?? t('common.ok')}
        </button>
      </div>
    </Modal>
  );
}
